import { isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import type {
  MultiOptimizationConfiguration,
  MultiOptimizationResponse,
  Portfolio,
  PortfolioPolicy,
  PortfolioSummary,
} from 'venn-api';
import { runAdHocMultiOptimization } from 'venn-api';
import { forceZeroResidualForecastChangedAtom } from 'venn-state';
import { logExceptionIntoSentry, type ObjectiveType } from 'venn-utils';
import { analyticsService, useApi, useHasFF } from 'venn-utils';

const useOptimization = (
  portfolio: Portfolio | undefined,
  optimizationPolicy: PortfolioPolicy | undefined,
  objective: ObjectiveType | undefined,
  objectiveValue: number | undefined,
  summary: PortfolioSummary | undefined,
  constraintBenchmarkPseudoPortfolio: Portfolio | undefined,
) => {
  const [response, setResponse] = useState<MultiOptimizationResponse | undefined>();
  const [loading, setLoading] = useState(false);
  const optimizeApiRef = useRef(useApi(runAdHocMultiOptimization));
  const forceZeroResidualForecastChanged = useRecoilValue(forceZeroResidualForecastChangedAtom);
  const hasPortfolioLabFF = useHasFF('portfolio_lab_ff');

  useEffect(() => {
    const optimize = async () => {
      if (isNil(portfolio) || isNil(optimizationPolicy) || isNil(objective) || isNil(objectiveValue)) {
        setResponse(undefined);
        return;
      }

      setLoading(true);
      try {
        const { content } = await optimizeApiRef.current(
          getRequest(
            portfolio,
            optimizationPolicy,
            objective,
            objectiveValue,
            constraintBenchmarkPseudoPortfolio,
            hasPortfolioLabFF,
          ),
        );
        const emptyResult =
          isNil(content.optimizedPortfolio.portfolio) &&
          content.alternatePortfolios.every((item) => isNil(item.portfolio));
        setResponse(emptyResult ? undefined : content);
        if (emptyResult) {
          analyticsService.portfolioLabNoSolutionsFound();
        }
        setLoading(false);
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e?.name !== 'AbortError') {
          setResponse(undefined);
          setLoading(false);
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          logExceptionIntoSentry(e);
        }
      }
    };

    optimize();
  }, [
    portfolio,
    optimizationPolicy,
    objective,
    objectiveValue,
    summary,
    constraintBenchmarkPseudoPortfolio,
    hasPortfolioLabFF,
    /**
     * This is not used directly inside useEffect, but is needed to rerun optimization
     * if "Force Zero Residual Forecast" has been toggled. Do not remove this dependency
     */
    forceZeroResidualForecastChanged,
  ]); // Summary changes when default forecast changes

  return {
    loadingOptimization: loading,
    optimizedResult: response?.optimizedPortfolio,
    alternateResults: response?.alternatePortfolios,
    averageInvestmentBuys: response?.averageInvestmentBuys,
    averageInvestmentSells: response?.averageInvestmentSells,
  };
};

export default useOptimization;

export const getRequest = (
  portfolio: Portfolio,
  optimizationPolicy: PortfolioPolicy,
  objective: ObjectiveType,
  objectiveValue: number,
  constraintBenchmarkPseudoPortfolio: Portfolio | undefined,
  hasPortfolioLabFF: boolean,
): Partial<MultiOptimizationConfiguration> => {
  const isMaximizeSharpe = objective === 'maximizeSharpe';
  const hasBenchmarkConstraint = !isNil(constraintBenchmarkPseudoPortfolio) && hasPortfolioLabFF;
  const constraints =
    hasBenchmarkConstraint && !isMaximizeSharpe
      ? optimizationPolicy.constraints?.map((constraint) =>
          constraint.constraintType === 'TRACKING_ERROR' ? { ...constraint, targets: [] } : constraint,
        )
      : optimizationPolicy.constraints?.filter((constraint) => constraint.constraintType !== 'TRACKING_ERROR');
  return {
    benchmark: !hasPortfolioLabFF || isMaximizeSharpe ? undefined : constraintBenchmarkPseudoPortfolio,
    constraints,
    portfolio,
    optimizationTarget:
      objective === 'maximizeReturns'
        ? 'MAXIMIZE_RETURN'
        : objective === 'targetReturn'
          ? 'MINIMIZE_VOLATILITY'
          : 'MAXIMIZE_SHARPE',
    maxVolatility: objective === 'maximizeReturns' ? objectiveValue / 100 : undefined,
    minReturn: objective === 'maximizeReturns' ? undefined : objectiveValue / 100,
    relaxationsPercent: 0.1,
    disableResidualForecasts: false,
  };
};
